<template>
  <div class="actus-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderCommunity :page="currentPageInfo" />
    <SearchResults
      :is-loading="isLoading"
      placeholder-text="Rechercher une actualité"
      @search-update="searchUpdateHandler"
    >
      <template #results>
        <CardContainer
          :items="communityActualities"
          :no-result-text="noContent"
        />
      </template>
    </SearchResults>
    <ThePagination
      :total-pages="communityActualitiesPages"
      :current-page="communityActualitiesCurrentPage"
      :go-to="changePageTo"
    />
    <PegiWarning v-if="community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadActualitiesCommunity = useLoadActualitiesCommunity()
    return loadActualitiesCommunity(to)
  },
})

const route = useRoute()

const actualitiesStore = useActualitiesStore()
const {
  communityActualities,
  communityActualitiesPages,
  communityActualitiesCurrentPage,
} = storeToRefs(actualitiesStore)

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)

const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const link = useLinkPrevNext({
  currentPage: communityActualitiesCurrentPage,
  pagesCount: communityActualitiesPages,
})

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: 'actualité de la communauté',
      route: '',
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: `Actus jeux vidéo ${community.value?.title} PS5, PS4, PlayStation 4 Pro`,
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  // nothing to list yet ("Aucun contenu"): kept out of Google, as out of the sitemap (CommunityController)
  robots: computed(() =>
    communityActualities.value.length ? undefined : 'noindex, follow',
  ),
  title: `Actus jeux vidéo ${community.value?.title} PS5, PS4, PlayStation 4 Pro`,
  description: `Découvrez toute l'actualité sur ${community.value?.title}. Rejoignez la communauté ${community.value?.title} pour plus d'informations.`,
  ogTitle: `Toute l’actualité sur ${community.value?.title}`,
  ogDescription: `Soyez au courant des dernières infos de la communauté sur ${community.value?.title}`,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const currentPageInfo = computed(() => {
  const { image, imageMobile } = getImages(community.value)

  return {
    title: 'Les actualités',
    game: community.value?.title,
    pegi: community.value?.pegi,
    members: community.value?.users_count,
    image,
    imageMobile,
    description: `Soyez au courant des dernières infos de la communauté sur ${community.value?.title}`,
    contribPoints: referential.value?.points.post_source_create,
  }
})

const searchData = async () => {
  isLoading.value = true

  await actualitiesStore.fetchCommunityActualities({
    page: 1,
    community: route.params.communityName as string,
    search: search.value,
  })

  if (!communityActualities.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const changePageTo = async (index = 1) => {
  if (index > 0) {
    await actualitiesStore.fetchCommunityActualities({
      page: index,
      search: search.value,
      community: route.params.communityName as string,
    })
  }
}
</script>

<style lang="scss" scoped>
.actus-page {
  min-height: 100vh;
  padding-bottom: 10rem;

  .title-line {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}
</style>
